import { useState } from "react";

import { RenderSeeMoreTextAnswers } from "~/common/components";
import { CompletedListJobsAnswer, CompletedRadioAnswer } from "~/common/utils";
import {
  intakeQuestionDataByCodes,
  YES_NO_PREFER_NOT_TO_SAY_TYPES,
} from "~/common/utils/consultations/intake";
import { screeningQuestionDataByCodes } from "~/common/utils/consultations/screening";
import { renderQuestionTitle } from "~/common/utils/renderQuestionTitle";
import { PronounsOptions } from "~/patients";
import { SymptomsListFormValues } from "~/patients/utils/consultations/intake";
import { formatRawResponse } from "~/providers/utils/consultationRequests/formatRawResponses";
import {
  AudioResponse,
  CompletedAISummary,
  CompletedAISummaryEvaluation,
  IntakeAnswer,
  IntakeQuestionType,
  MultiMedicationInThePastResponse,
  MultiPsychMedicationInThePastResponse,
  RawResponsesType,
  RESPONSE_QUESTION_TYPES,
  ScreeningAnswer,
  ScreeningQuestionType,
  YesNoNaWithAudioResponse,
} from "~/providers/utils/types/rawResponsesTypes";
import { RawResponseLayout } from "./RawResponseLayout";
import { AudioAnswer } from "./rawResponses/AudioAnswer";
import { MultiMedicationInThePast } from "./rawResponses/MultiMedicationInThePast";
import { MultiPsychMedicationInThePast } from "./rawResponses/MultiPsychMedicationInThePast";
import { PastJobsAnswer } from "./rawResponses/PastJobsAnswer";
import { SymptomsAnswer } from "./rawResponses/SymptomsAnswer";
import { TextAnswer } from "./rawResponses/TextAnswer";
import { YesNoNaWithAudioAnswer } from "./rawResponses/YesNoNaWithAudioAnswer";

interface RawResponsesRenderProps {
  answer: ScreeningAnswer | IntakeAnswer;
  isIntake: boolean;
  pronouns?: PronounsOptions;
}

export const RawResponsesRender = ({
  answer: answerObject,
  isIntake,
  pronouns,
}: RawResponsesRenderProps) => {
  const [seeMoreActive, setSeeMoreActive] = useState(false);

  return Object.entries(answerObject).map(([questionCode, answer]) => {
    const isAnswersArray = Array.isArray(answer) && answer.length > 0;
    const questionData = isIntake
      ? intakeQuestionDataByCodes[questionCode as IntakeQuestionType]
      : screeningQuestionDataByCodes[questionCode as ScreeningQuestionType];

    const renderAnswer = (answerObject: ScreeningAnswer | IntakeAnswer) => {
      if (answerObject.type === RESPONSE_QUESTION_TYPES.yes_no_not_say) {
        const response = (answer as CompletedRadioAnswer[])?.[0]?.value;

        if (!response) return "";

        return response === YES_NO_PREFER_NOT_TO_SAY_TYPES.prefer_not_to_say
          ? "I prefer not to say"
          : response.replace(/_/g, " ");
      }

      if (answerObject.type === RESPONSE_QUESTION_TYPES.audio)
        return (
          <AudioAnswer
            questionCode={questionCode}
            answers={answer as AudioResponse[]}
          />
        );

      if (
        answerObject.type ===
        RESPONSE_QUESTION_TYPES.yes_no_na_with_audio_on_yes
      )
        return (
          <YesNoNaWithAudioAnswer
            questionCode={questionCode}
            answers={answer as YesNoNaWithAudioResponse[]}
          />
        );

      if (
        answerObject.type ===
        RESPONSE_QUESTION_TYPES.multi_medication_in_the_past
      )
        return (
          <MultiMedicationInThePast
            questionCode={questionCode}
            answers={answer as MultiMedicationInThePastResponse[]}
          />
        );

      if (
        answerObject.type ===
        RESPONSE_QUESTION_TYPES.multi_psych_medication_in_the_past
      )
        return (
          <MultiPsychMedicationInThePast
            questionCode={questionCode}
            answers={answer as MultiPsychMedicationInThePastResponse[]}
          />
        );

      if (
        answerObject.type ===
        RESPONSE_QUESTION_TYPES.jobs_you_have_held_in_the_past
      ) {
        return <PastJobsAnswer answers={answer as CompletedListJobsAnswer[]} />;
      }

      const resolvedTitle =
        typeof questionData?.title === "function"
          ? questionData.title()
          : (questionData?.title ?? "");

      return (
        <TextAnswer
          questionCode={questionCode}
          answers={formatRawResponse(
            answerObject.type,
            answer as RawResponsesType,
            questionCode,
            answerObject.question_text ?? resolvedTitle,
          )}
        />
      );
    };

    if (
      isIntake &&
      isAnswersArray &&
      answerObject.type ===
        RESPONSE_QUESTION_TYPES.symptoms_experienced_multi_select
    ) {
      return (
        <SymptomsAnswer
          baseQuestion={renderQuestionTitle(questionData?.title, pronouns)}
          key={questionCode}
          answers={answer as SymptomsListFormValues[]}
        />
      );
    }

    if (
      isIntake &&
      isAnswersArray &&
      answerObject.type ===
        RESPONSE_QUESTION_TYPES.ai_informant_summary_evaluation
    ) {
      const onClickSeeMoreButton = () => {
        setSeeMoreActive((prev) => !prev);
      };

      return (
        <>
          <RawResponseLayout
            question="On a scale of 0 to 5, how well does this summary describe you?"
            key={`${questionCode}-1`}
          >
            <div className="flex items-center gap-2">
              <span>{(answer[0] as CompletedAISummaryEvaluation).value}</span>
              <span className="italic">(0 = not at all, 5 = completely)</span>
            </div>
          </RawResponseLayout>
          <RawResponseLayout
            question="How would you modify it?"
            key={`${questionCode}-2`}
          >
            <RenderSeeMoreTextAnswers
              seeMoreActive={seeMoreActive}
              onClick={onClickSeeMoreButton}
              text={(answer[0] as CompletedAISummaryEvaluation).modifications}
            />
          </RawResponseLayout>
        </>
      );
    }

    if (
      isIntake &&
      isAnswersArray &&
      answerObject.type === RESPONSE_QUESTION_TYPES.ai_informant_summary
    ) {
      return (
        <RawResponseLayout
          question={
            <div className="flex flex-col gap-1">
              <div>{renderQuestionTitle(questionData?.title)}</div>
              <div className="text-xs text-gray-35">
                {(answer[0] as CompletedAISummary).prompt}
              </div>
            </div>
          }
          key={questionCode}
        >
          <div className="h-full">
            {(answer[0] as CompletedAISummary).value
              ? renderAnswer(answerObject)
              : "(Skipped)"}
          </div>
        </RawResponseLayout>
      );
    }

    return (
      <RawResponseLayout
        question={answerObject.question_text ?? renderQuestionTitle(questionData?.title, pronouns, questionCode)}
        key={questionCode}
      >
        {isAnswersArray && renderAnswer(answerObject)}
      </RawResponseLayout>
    );
  });
};
